// src/app/features/adminAuthSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AdminUser } from "../api/adminAuthApi";
import { RootState } from "../store";

/* ─────────────────────────────────────────────────────────────────
   STATE
───────────────────────────────────────────────────────────────── */

export interface AdminAuthState {
  token?: string;
  user?: AdminUser;
  hydrated: boolean;
}

/* ─────────────────────────────────────────────────────────────────
   STORAGE
───────────────────────────────────────────────────────────────── */

const AUTH_TTL_MS = 8 * 60 * 60 * 1000; // 8-hour admin session (shorter than public)
const STORAGE_KEY = "admin_auth_state";
const STORAGE_EXPIRES_KEY = "admin_auth_expires";

/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

const safePersist = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    console.warn("adminAuth: localStorage unavailable", err);
  }
};

const safeClear = (...keys: string[]) => {
  keys.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
  });
};

/* ─────────────────────────────────────────────────────────────────
   INITIAL STATE
───────────────────────────────────────────────────────────────── */

const initialState: AdminAuthState = {
  token: undefined,
  user: undefined,
  hydrated: false,
};

/* ─────────────────────────────────────────────────────────────────
   SLICE
───────────────────────────────────────────────────────────────── */

const adminAuthSlice = createSlice({
  name: "adminAuth",
  initialState,
  reducers: {
    /**
     * Called after a successful adminLogin or superAdminRegister.
     */
    setAdminCredentials(
      state,
      action: PayloadAction<{ token: string; user: AdminUser }>,
    ) {
      const { token, user } = action.payload;
      state.token = token;
      state.user = user;
      state.hydrated = true;

      safePersist(STORAGE_KEY, JSON.stringify({ token, user }));
      safePersist(STORAGE_EXPIRES_KEY, String(Date.now() + AUTH_TTL_MS));
    },

    /**
     * Partial user update — used after profile edits.
     * The backend always returns the full updated user so we replace wholesale.
     */
    updateAdminUser(state, action: PayloadAction<AdminUser>) {
      state.user = action.payload;
      safePersist(
        STORAGE_KEY,
        JSON.stringify({ token: state.token, user: action.payload }),
      );
    },

    /**
     * Full sign-out: clears Redux state and localStorage.
     */
    clearAdminCredentials(state) {
      state.token = undefined;
      state.user = undefined;
      state.hydrated = true;
      safeClear(STORAGE_KEY, STORAGE_EXPIRES_KEY);
    },

    /**
     * Called once on app startup (e.g. in AdminApp.tsx) to rehydrate
     * the session from localStorage.
     */
    loadAdminFromStorage(state) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const expires = Number(
          localStorage.getItem(STORAGE_EXPIRES_KEY) || "0",
        );

        if (!raw || !expires || Date.now() > expires) {
          state.token = undefined;
          state.user = undefined;
          state.hydrated = true;
          safeClear(STORAGE_KEY, STORAGE_EXPIRES_KEY);
          return;
        }

        const parsed = JSON.parse(raw) as {
          token?: string;
          user?: AdminUser;
        };
        state.token = parsed.token;
        state.user = parsed.user;
        state.hydrated = true;
      } catch {
        state.token = undefined;
        state.user = undefined;
        state.hydrated = true;
      }
    },
  },
});

export const {
  setAdminCredentials,
  updateAdminUser,
  clearAdminCredentials,
  loadAdminFromStorage,
} = adminAuthSlice.actions;

export default adminAuthSlice.reducer;

/* ─────────────────────────────────────────────────────────────────
   SELECTORS
───────────────────────────────────────────────────────────────── */

export const selectAdminToken = (state: RootState) => state.adminAuth.token;
export const selectAdminUser = (state: RootState) => state.adminAuth.user;
export const selectAdminHydrated = (state: RootState) =>
  state.adminAuth.hydrated;
export const selectIsSuperAdmin = (state: RootState) =>
  state.adminAuth.user?.role === "super_admin";
export const selectAdminRole = (state: RootState) => state.adminAuth.user?.role;
